import { Fragment, useEffect, useState } from "react";
import { fetchBreakdown, send, type BreakdownData, type TemplateStep } from "../api.js";

const DISCIPLINES = ["01-DFTGM", "01-DFTGE"];

/**
 * The breakdown a new project arrives with.
 *
 * Every step's date is an offset from the project due date, never a date of its
 * own — a template that carried real dates would be wrong the day after it was
 * written. Offsets are what let the morning file create a project that is
 * already schedulable instead of a project that is a piece of work.
 */
export function Templates() {
  const [data, setData] = useState<BreakdownData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState<string | null>(null);
  const [draft, setDraft] = useState<TemplateStep[] | null>(null);

  const load = async () => {
    try { setData(await fetchBreakdown()); setErr(null); }
    catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { void load(); }, []);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); await load(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  if (err && !data) return <div className="err">{err}</div>;
  if (!data) return <p className="muted">Loading…</p>;

  const readOnly = data.user.role !== "admin";
  const current = data.templates.find((t) => t.templateId === sel) ?? null;
  const steps = draft ?? current?.steps ?? [];
  const dirty = draft !== null;

  const usedFor = (productCode: string | null) =>
    data.projects.filter((p) => p.productCode === productCode).length;

  const edit = (seq: number, patch: Partial<TemplateStep>) =>
    setDraft(steps.map((s) => (s.seq === seq ? { ...s, ...patch } : s)));

  const addStep = () =>
    setDraft([...steps, {
      seq: steps.reduce((n, s) => Math.max(n, s.seq), 0) + 1,
      phaseCode: "New step",
      workType: "Detail",
      discipline: "01-DFTGM",
      estHours: 8,
      skillId: null,
      minProficiency: null,
      dueOffsetDays: 0,
      dependsOnSeq: steps.length ? steps[steps.length - 1]!.seq : null,
    }]);

  return (
    <div>
      <p className="hint" style={{ marginTop: 0 }}>
        One template per product code, plus an optional catch-all with no code. A new project
        in the morning file gets the template matching its product code, and arrives with its
        steps already dated from its due date.
      </p>
      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}
      {!data.skills.length && (
        <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
          No skills have been defined yet, so the Skill column has nothing to offer. Add them
          on the <b>Skills</b> screen and they become selectable here.
        </p>
      )}

      <div className="dsl">
        <div className="dsl-list">
          <div className="dsl-grp">{data.templates.length} template{data.templates.length === 1 ? "" : "s"}</div>
          {data.templates.map((t) => (
            <div key={t.templateId}
              className={`dsl-i${sel === t.templateId ? " on" : ""}${t.active ? "" : " off"}`}
              onClick={() => { setSel(t.templateId); setDraft(null); }}>
              {t.name}
              <span className="t">{t.productCode ?? "catch-all"}</span>
            </div>
          ))}
          {!readOnly && (
            <button className="btn" style={{ margin: 8 }} disabled={busy}
              onClick={() => void act(async () => {
                const name = prompt("Template name, e.g. Standard SSYS");
                if (!name) return;
                const code = prompt("Product code it applies to (blank = catch-all)") ?? "";
                const r = await send<{ templateId: string }>("/templates", "POST", {
                  name, productCode: code.trim() || null,
                });
                setSel(r.templateId); setDraft(null);
              })}>
              New template
            </button>
          )}
        </div>

        <div className="dsl-ed">
          {!current ? (
            <p className="muted">Pick a template.</p>
          ) : (
            // Keyed so the uncontrolled name / product code inputs remount per template.
            <Fragment key={current.templateId}>
              <div className="fld2">
                <div className="fld"><span>name</span>
                  <input defaultValue={current.name} disabled={readOnly}
                    onBlur={(e) => e.target.value !== current.name && void act(() =>
                      send(`/templates/${current.templateId}`, "PATCH", { name: e.target.value }))} />
                </div>
                <div className="fld"><span>product code · blank is the catch-all</span>
                  <input defaultValue={current.productCode ?? ""} disabled={readOnly}
                    onBlur={(e) => (e.target.value || null) !== current.productCode && void act(() =>
                      send(`/templates/${current.templateId}`, "PATCH", { productCode: e.target.value || null }))} />
                </div>
              </div>
              <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
                {current.productCode
                  ? `${usedFor(current.productCode)} existing project(s) carry this product code.`
                  : "Used for any project whose product code matches no other template."}
              </p>

              <div className="scroll tall">
                <table className="qt" style={{ minWidth: 780 }}>
                  <thead>
                    <tr>
                      <th className="r">#</th><th>Step</th><th>Work type</th><th>Disc</th>
                      <th className="r">Est</th><th>Skill</th><th className="r">Lvl</th>
                      <th className="r">Due offset</th><th className="r">After</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {steps.map((s) => (
                      <tr key={s.seq}>
                        <td className="r mono">{s.seq}</td>
                        <td>
                          <input value={s.phaseCode ?? ""} disabled={readOnly}
                            onChange={(e) => edit(s.seq, { phaseCode: e.target.value })} />
                        </td>
                        <td>
                          <select value={s.workType} disabled={readOnly}
                            onChange={(e) => edit(s.seq, { workType: e.target.value })}>
                            {data.workTypes.filter((w) => w.active || w.code === s.workType)
                              .map((w) => <option key={w.code} value={w.code}>{w.label}</option>)}
                          </select>
                        </td>
                        <td>
                          <select value={s.discipline ?? ""} disabled={readOnly}
                            onChange={(e) => edit(s.seq, { discipline: e.target.value || null })}>
                            <option value="">—</option>
                            {DISCIPLINES.map((d) => (
                              <option key={d} value={d}>{d.replace("01-DFTG", "")}</option>
                            ))}
                          </select>
                        </td>
                        <td className="r">
                          <input className="num" type="number" step="0.5" min="0" value={s.estHours}
                            disabled={readOnly}
                            onChange={(e) => edit(s.seq, { estHours: Number(e.target.value) })} />
                        </td>
                        <td>
                          {data.skills.length ? (
                            <select value={s.skillId ?? ""} disabled={readOnly}
                              onChange={(e) => edit(s.seq, {
                                skillId: e.target.value || null,
                                minProficiency: e.target.value ? (s.minProficiency ?? 2) : null,
                              })}>
                              <option value="">— anyone —</option>
                              {data.skills.map((k) => (
                                <option key={k.skillId} value={k.skillId}>{k.name}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="muted" style={{ fontSize: 12 }}>no skills yet</span>
                          )}
                        </td>
                        <td className="r">
                          {s.skillId ? (
                            <select value={s.minProficiency ?? 1} disabled={readOnly}
                              onChange={(e) => edit(s.seq, { minProficiency: Number(e.target.value) })}>
                              <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
                            </select>
                          ) : <span className="muted">—</span>}
                        </td>
                        <td className="r">
                          <input className="num" type="number" step="1" value={s.dueOffsetDays}
                            disabled={readOnly}
                            title="Days from the project due date. −30 means a month before it."
                            onChange={(e) => edit(s.seq, { dueOffsetDays: Number(e.target.value) })} />
                          <span className="muted" style={{ fontSize: 11 }}> d</span>
                        </td>
                        <td className="r">
                          <select value={s.dependsOnSeq ?? ""} disabled={readOnly}
                            onChange={(e) => edit(s.seq, {
                              dependsOnSeq: e.target.value ? Number(e.target.value) : null,
                            })}>
                            <option value="">—</option>
                            {steps.filter((o) => o.seq !== s.seq).map((o) => (
                              <option key={o.seq} value={o.seq}>{o.seq}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {!readOnly && (
                            <button className="btn"
                              onClick={() => setDraft(steps.filter((o) => o.seq !== s.seq))}>×</button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {!steps.length && (
                      <tr><td colSpan={10} className="muted">No steps yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {!readOnly && (
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button className="btn" onClick={addStep}>Add a step</button>
                  <span className="spacer" />
                  {dirty && <button className="btn" onClick={() => setDraft(null)}>Discard</button>}
                  <button className="btn on" disabled={!dirty || busy}
                    onClick={() => void act(async () => {
                      await send(`/templates/${current.templateId}/steps`, "PUT", { steps });
                      setDraft(null);
                    })}>
                    {dirty ? "Save the steps" : "Saved"}
                  </button>
                  <button className="btn" disabled={busy}
                    onClick={() => void act(async () => {
                      if (!confirm(`Delete the template “${current.name}”?`)) return;
                      await send(`/templates/${current.templateId}`, "DELETE");
                      setSel(null); setDraft(null);
                    })}>
                    Delete
                  </button>
                </div>
              )}
            </Fragment>
          )}
        </div>
      </div>
    </div>
  );
}
